<template>
  <div class="bg-n-slate-2 dark:bg-n-slate-3 rounded-xl shadow-sm border border-n-weak">
    <div class="px-6 py-4 border-b border-n-weak">
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ title }} ({{ agents.length }})
        </h3>
        <button
          @click="toggleExpanded"
          class="text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
        >
          {{ expanded ? 'Minimizar' : 'Expandir' }}
        </button>
      </div>
    </div>

    <div v-if="expanded" class="overflow-x-auto">
      <table class="min-w-full divide-y divide-n-weak">
        <thead class="bg-n-slate-1 dark:bg-n-slate-2">
          <tr>
            <th
              @click="sortBy('name')"
              class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-3 dark:hover:bg-n-slate-4 transition-colors"
            >
              <div class="flex items-center gap-2">
                Agente
                <svg v-if="sortKey === 'name'" class="w-4 h-4" :class="{ 'rotate-180': sortOrder === 'desc' }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                </svg>
              </div>
            </th>
            <th
              @click="sortBy('open_count')"
              class="px-6 py-3 text-center text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-3 dark:hover:bg-n-slate-4 transition-colors"
            >
              <div class="flex items-center justify-center gap-2">
                Abertas
                <svg v-if="sortKey === 'open_count'" class="w-4 h-4" :class="{ 'rotate-180': sortOrder === 'desc' }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                </svg>
              </div>
            </th>
            <th class="px-6 py-3 text-center text-xs font-medium text-n-slate-11 uppercase tracking-wider">
              Pendentes
            </th>
            <th class="px-6 py-3 text-center text-xs font-medium text-n-slate-11 uppercase tracking-wider">
              Resolvidas
            </th>
            <th
              @click="sortBy('capacity_percentage')"
              class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-3 dark:hover:bg-n-slate-4 transition-colors"
            >
              <div class="flex items-center gap-2">
                Capacidade
                <svg v-if="sortKey === 'capacity_percentage'" class="w-4 h-4" :class="{ 'rotate-180': sortOrder === 'desc' }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                </svg>
              </div>
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider">
              Performance
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider">
              Última Conversa
            </th>
          </tr>
        </thead>
        <tbody class="bg-n-slate-2 dark:bg-n-slate-3 divide-y divide-n-weak">
          <template v-for="agent in sortedAgents" :key="agent.id">
          <tr
            @click="handleAgentClick(agent.id)"
            class="hover:bg-n-slate-3 dark:hover:bg-n-slate-4 transition-colors cursor-pointer"
            :class="{
              'opacity-60': status === 'offline',
              'bg-n-blue-3 dark:bg-n-blue-4 border-l-4 border-n-blue-9': isAgentSelected(agent.id),
              'ring-2 ring-n-blue-9 ring-inset': isAgentSelected(agent.id)
            }"
          >
            <!-- Agent Info -->
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex items-center">
                <div class="flex-shrink-0 h-10 w-10">
                  <img
                    v-if="agent.avatar_url"
                    :src="agent.avatar_url"
                    :alt="agent.name"
                    class="h-10 w-10 rounded-full"
                  />
                  <div
                    v-else
                    class="h-10 w-10 rounded-full bg-gradient-to-br from-n-blue-9 to-n-iris-9 flex items-center justify-center text-white font-semibold"
                  >
                    {{ getInitials(agent.name) }}
                  </div>
                </div>
                <div class="ml-4">
                  <div class="text-sm font-medium text-n-slate-12">
                    {{ agent.name }}
                  </div>
                  <div class="text-xs text-n-slate-10">
                    {{ agent.email }}
                  </div>

                  <!-- STATUS DO AGENTE -->
                  <!-- Online: disponível para receber conversas -->
                  <div v-if="agent.availability === 'online'" class="text-xs text-n-teal-11 mt-1">
                    🟢 Online há {{ formatDuration(agent.online_since) }}
                  </div>

                  <!-- Busy/Ocupado: NÃO recebe novas conversas -->
                  <div v-else-if="agent.availability === 'busy'" class="mt-1">
                    <div class="flex items-center gap-2">
                      <span class="text-xs text-n-amber-11">
                        🟡 Ocupado há {{ formatDuration(agent.online_since) }}
                      </span>
                      <span class="text-xs px-1.5 py-0.5 rounded bg-n-amber-3 text-n-amber-11 dark:bg-n-amber-4 dark:text-n-amber-10 font-medium">
                        Não recebe conversas
                      </span>
                    </div>
                  </div>

                  <!-- Offline: mostra há quanto tempo está offline (baseado no histórico) -->
                  <div v-else-if="agent.last_seen && agentHistories[agent.id]?.[0]?.duration" class="text-xs text-n-slate-10 mt-1">
                    ⚫ Offline há {{ getAgentOfflineDuration(agent) }}
                  </div>

                  <!-- Botão para expandir histórico - disponível para TODOS os agentes -->
                  <button
                    @click.stop="toggleHistory(agent.id)"
                    class="text-xs text-n-blue-11 hover:text-n-blue-12 mt-1 flex items-center gap-1"
                  >
                    <svg
                      class="w-3 h-3 transition-transform"
                      :class="{ 'rotate-90': expandedHistories[agent.id] }"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                    {{ expandedHistories[agent.id] ? 'Ocultar' : 'Ver' }} histórico
                  </button>
                </div>
              </div>
            </td>

            <!-- Open Count -->
            <td class="px-6 py-4 whitespace-nowrap text-center">
              <span
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold"
                :class="getOpenCountClass(agent)"
              >
                {{ agent.open_count }}
              </span>
            </td>

            <!-- Pending Count -->
            <td class="px-6 py-4 whitespace-nowrap text-center">
              <span class="text-sm text-n-amber-11 dark:text-n-amber-10 font-semibold">
                {{ agent.pending_count }}
              </span>
            </td>

            <!-- Resolved Count -->
            <td class="px-6 py-4 whitespace-nowrap text-center">
              <span class="text-sm text-n-teal-11 dark:text-n-teal-10 font-semibold">
                {{ agent.resolved_count }}
              </span>
            </td>

            <!-- Capacity -->
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="w-32">
                <div class="flex items-center justify-between text-xs text-n-slate-11 mb-1">
                  <span class="font-medium">{{ agent.open_count }}/{{ agent.limit }}</span>
                  <span class="font-semibold">{{ agent.capacity_percentage }}%</span>
                </div>
                <div class="h-2 bg-n-slate-4 dark:bg-n-slate-6 rounded-full overflow-hidden">
                  <div
                    class="h-full rounded-full transition-all duration-300"
                    :class="getCapacityBarClass(agent.capacity_percentage)"
                    :style="{ width: `${Math.min(agent.capacity_percentage, 100)}%` }"
                  ></div>
                </div>
              </div>
            </td>

            <!-- Performance -->
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-xs space-y-1">
                <div class="flex items-center gap-2">
                  <span class="text-n-slate-11">Taxa resolução:</span>
                  <span class="font-semibold text-n-slate-12">
                    {{ agent.performance.resolution_rate }}%
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-n-slate-11">Total atribuídas:</span>
                  <span class="font-semibold text-n-slate-12">
                    {{ agent.performance.total_handled }}
                  </span>
                </div>
              </div>
            </td>

            <!-- Last Conversation -->
            <td class="px-6 py-4">
              <div v-if="agent.last_conversation" class="text-xs">
                <div class="flex items-center gap-2 mb-1">
                  <!-- 🔥 BOTÃO COM ÍCONE para ir na conversa -->
                  <button
                    v-tooltip="'Abrir conversa #' + agent.last_conversation.display_id"
                    class="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-n-blue-11 hover:text-n-blue-12 hover:bg-n-blue-3 dark:hover:bg-n-blue-4 transition-colors font-medium"
                    @click.stop="navigateToConversation(agent.last_conversation.display_id)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    <span>#{{ agent.last_conversation.display_id }}</span>
                  </button>
                  
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                    :class="getStatusClass(agent.last_conversation.status)"
                  >
                    {{ getStatusLabel(agent.last_conversation.status) }}
                  </span>
                </div>
                <div class="text-n-slate-10">
                  {{ agent.last_conversation.time_ago }}
                </div>
              </div>
              <div v-else class="text-xs text-n-slate-10">
                Nenhuma conversa
              </div>
            </td>
          </tr>

          <!-- 🔥 Histórico completo de status (online + busy + offline) -->
          <tr v-if="expandedHistories[agent.id]">
            <td colspan="7" class="px-6 py-4 bg-n-slate-1 dark:bg-n-slate-2">
              <div class="ml-14">
                <div class="text-sm font-semibold text-n-slate-12 mb-3">
                  📊 Histórico de Status
                </div>

                <!-- Loading state -->
                <div v-if="loadingHistories[agent.id]" class="text-center py-4 text-n-slate-11">
                  Carregando histórico...
                </div>

                <!-- Todos os períodos (online, busy, offline) -->
                <div v-else-if="agentHistories[agent.id] && agentHistories[agent.id].length > 0" class="space-y-2">
                  <div
                    v-for="(period, index) in agentHistories[agent.id]"
                    :key="index"
                    class="flex flex-col gap-2 text-sm py-3 px-3 rounded-lg bg-n-slate-2 dark:bg-n-slate-3 border-l-4"
                    :class="getHistoryPeriodBorderClass(period, index)"
                  >
                    <!-- Primeira linha: Status + Duração -->
                    <div class="flex items-center gap-4">
                      <!-- Indicador de status com cor e emoji corretos -->
                      <div class="flex items-center gap-2 min-w-[100px]">
                        <!-- Online -->
                        <span v-if="period.status === 'online'" class="text-n-teal-11">🟢</span>
                        <!-- Busy -->
                        <span v-else-if="period.status === 'busy'" class="text-n-amber-11">🟡</span>
                        <!-- Offline -->
                        <span v-else class="text-n-slate-11">⚫</span>

                        <span class="font-medium text-n-slate-12">
                          {{ 
                            period.status === 'online' ? 'Online' : 
                            period.status === 'busy' ? 'Ocupado' : 
                            'Offline'
                          }}
                        </span>
                      </div>

                      <!-- Duração -->
                      <div class="text-n-slate-11 flex items-center gap-1">
                        <span>Duração:</span>
                        <span class="font-semibold text-n-slate-12">
                          {{ formatHistoryDuration(period.duration) }}
                        </span>
                      </div>

                      <!-- Indicador "Atual" apenas no primeiro período se ainda estiver nesse status -->
                      <div v-if="period.is_current && index === 0" class="ml-auto">
                        <span
                          class="text-xs px-2 py-1 rounded font-medium"
                          :class="{
                            'bg-n-teal-3 text-n-teal-11 dark:bg-n-teal-4 dark:text-n-teal-10': period.status === 'online',
                            'bg-n-amber-3 text-n-amber-11 dark:bg-n-amber-4 dark:text-n-amber-10': period.status === 'busy',
                            'bg-n-ruby-3 text-n-ruby-11 dark:bg-n-ruby-4 dark:text-n-ruby-10': period.status === 'offline'
                          }"
                        >
                          {{ 
                            period.status === 'online' ? 'Ainda online' :
                            period.status === 'busy' ? 'Ainda ocupado' :
                            'Ainda offline'
                          }}
                        </span>
                      </div>
                    </div>

                    <!-- Segunda linha: Período ou início (dependendo se está ativo) -->
                    <div v-if="!period.is_current || index !== 0" class="text-xs text-n-slate-10 ml-7">
                      {{ formatPeriodRange(period.start_time, period.end_time) }}
                    </div>
                    <div v-else class="text-xs text-n-slate-10 ml-7">
                      Início: {{ formatHistoryTimestamp(period.start_time) }}
                    </div>
                  </div>
                </div>

                <!-- Empty state -->
                <div v-else class="text-center py-4 text-n-slate-10">
                  <span class="text-2xl mb-2 block">✨</span>
                  Nenhum histórico registrado nas últimas horas
                </div>
              </div>
            </td>
          </tr>
          </template>
        </tbody>
      </table>

      <!-- Empty State -->
      <div v-if="agents.length === 0" class="text-center py-12">
        <div class="text-4xl mb-4">👤</div>
        <p class="text-n-slate-11">
          Nenhum agente {{ status === 'online' ? 'online' : 'offline' }} no momento
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import ReportsAPI from 'dashboard/api/reports';

export default {
  name: 'AgentsTable',
  props: {
    title: {
      type: String,
      required: true,
    },
    agents: {
      type: Array,
      required: true,
    },
    status: {
      type: String,
      default: 'online',
    },
    searchQuery: {
      type: String,
      default: '',
    },
    selectedAgentId: {
      type: Number,
      default: null,
    },
    teamId: {
      type: Number,
      default: null,
    },
    lastUpdate: {
      type: [Date, String],
      default: null,
    },
  },
  emits: ['agent-click'],
  setup(props, { emit }) {
    const router = useRouter();
    const currentAccountId = useMapGetter('getCurrentAccountId');
    const accountId = computed(() => currentAccountId.value);

    const expanded = ref(true);
    const sortKey = ref('open_count');
    const sortOrder = ref('desc');
    const expandedHistories = ref({});
    const agentHistories = ref({});
    const loadingHistories = ref({});

    const sortedAgents = computed(() => {
      const sorted = [...props.agents].sort((a, b) => {
        let aVal = a[sortKey.value];
        let bVal = b[sortKey.value];

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (sortOrder.value === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      return sorted;
    });

    const toggleExpanded = () => {
      expanded.value = !expanded.value;
    };

    const sortBy = (key) => {
      if (sortKey.value === key) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortKey.value = key;
        sortOrder.value = 'desc';
      }
    };

    const getInitials = (name) => {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };

    const formatDuration = (timestamp) => {
      if (!timestamp) return 'N/A';
      
      const now = new Date();
      const time = new Date(timestamp);
      const diff = Math.floor((now - time) / 1000);

      if (diff < 60) return `${diff}s`;
      if (diff < 3600) return `${Math.floor(diff / 60)}min`;
      if (diff < 86400) {
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        return minutes > 0 ? `${hours}h${minutes}min` : `${hours}h`;
      }
      
      const days = Math.floor(diff / 86400);
      return `${days} dia${days > 1 ? 's' : ''}`;
    };

    const navigateToConversation = (displayId) => {
      if (!displayId) {
        console.error('[AgentsTable] display_id é obrigatório');
        return;
      }

      router.push({
        name: 'inbox_conversation',
        params: {
          accountId: accountId.value,
          conversation_id: displayId,
        },
      });
    };

    const getOpenCountClass = (agent) => {
      const percentage = agent.capacity_percentage;
      if (percentage >= 100) {
        return 'bg-n-ruby-3 text-n-ruby-11 dark:bg-n-ruby-4 dark:text-n-ruby-10';
      } else if (percentage >= 80) {
        return 'bg-n-amber-3 text-n-amber-11 dark:bg-n-amber-4 dark:text-n-amber-10';
      } else if (agent.open_count === 0) {
        return 'bg-n-slate-3 text-n-slate-11 dark:bg-n-slate-5 dark:text-n-slate-10';
      } else {
        return 'bg-n-teal-3 text-n-teal-11 dark:bg-n-teal-4 dark:text-n-teal-10';
      }
    };

    const getCapacityBarClass = (percentage) => {
      if (percentage >= 100) {
        return 'bg-n-ruby-9';
      } else if (percentage >= 80) {
        return 'bg-n-amber-9';
      } else {
        return 'bg-n-teal-9';
      }
    };

    const getStatusClass = (status) => {
      const classes = {
        open: 'bg-n-blue-3 text-n-blue-11 dark:bg-n-blue-4 dark:text-n-blue-10',
        pending: 'bg-n-amber-3 text-n-amber-11 dark:bg-n-amber-4 dark:text-n-amber-10',
        resolved: 'bg-n-teal-3 text-n-teal-11 dark:bg-n-teal-4 dark:text-n-teal-10',
        snoozed: 'bg-n-slate-3 text-n-slate-11 dark:bg-n-slate-5 dark:text-n-slate-10',
      };
      return classes[status] || classes.open;
    };

    const getStatusLabel = (status) => {
      const labels = {
        open: 'Aberta',
        pending: 'Pendente',
        resolved: 'Resolvida',
        snoozed: 'Adiada',
      };
      return labels[status] || status;
    };

    const handleAgentClick = (agentId) => {
      emit('agent-click', agentId);
    };

    const isAgentSelected = (agentId) => {
      return props.selectedAgentId === agentId;
    };

    const toggleHistory = async (agentId) => {
      const isExpanded = expandedHistories.value[agentId];

      if (isExpanded) {
        expandedHistories.value[agentId] = false;
      } else {
        expandedHistories.value[agentId] = true;

        if (!agentHistories.value[agentId]) {
          await fetchAgentHistory(agentId);
        }
      }
    };

    const fetchAgentHistory = async (agentId) => {
      try {
        loadingHistories.value[agentId] = true;

        const response = await ReportsAPI.getAgentStatusHistory({
          accountId: accountId.value,
          agentIds: [agentId],
          limit: 20,
          teamId: props.teamId
        });

        const periods = response.data.offline_periods[agentId] || [];

        agentHistories.value[agentId] = periods;
      } catch (error) {
        console.error('[AgentsTable] ❌ Error fetching history:', error);
        agentHistories.value[agentId] = [];
      } finally {
        loadingHistories.value[agentId] = false;
      }
    };

    const formatHistoryTimestamp = (timestamp, includeSeconds = true) => {
      const date = new Date(timestamp * 1000);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      if (includeSeconds) {
        return `${day}/${month} ${hours}:${minutes}:${seconds}`;
      }
      return `${day}/${month} ${hours}:${minutes}`;
    };

    const formatPeriodRange = (startTime, endTime) => {
      return `Período: ${formatHistoryTimestamp(startTime)} às ${formatHistoryTimestamp(endTime)}`;
    };

    const formatHistoryDuration = (seconds) => {
      if (!seconds) return 'N/A';

      if (seconds < 60) return `${seconds}s`;
      if (seconds < 3600) return `${Math.floor(seconds / 60)}min`;
      if (seconds < 86400) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return minutes > 0 ? `${hours}h${minutes}min` : `${hours}h`;
      }

      const days = Math.floor(seconds / 86400);
      return `${days} dia${days > 1 ? 's' : ''}`;
    };

    // 🔥 Get agent offline duration (APENAS do histórico)
    const getAgentOfflineDuration = (agent) => {
      // Pega do histórico - primeiro período com status 'offline'
      const histories = agentHistories.value[agent.id] || [];
      const offlinePeriod = histories.find(p => p.status === 'offline' || !p.status);
      if (offlinePeriod?.duration) {
        return formatHistoryDuration(offlinePeriod.duration);
      }

      // Se não tem histórico, não exibe (controlado pela v-if no template)
      return '';
    };

    // Retorna a classe de borda baseado no status do período
    const getHistoryPeriodBorderClass = (period, index) => {
      if (period.is_current && index === 0) {
        if (period.status === 'online') return 'border-n-teal-9';
        if (period.status === 'busy') return 'border-n-amber-9';
        return 'border-n-ruby-9';
      }

      // Períodos antigos
      if (period.status === 'online') return 'border-n-teal-6';
      if (period.status === 'busy') return 'border-n-amber-6';
      return 'border-n-slate-6';
    };

    // Verifica se o agente precisa de histórico carregado (offline ou busy)
    const needsHistoryLoaded = (agent) => {
      return agent.availability === 'offline' || agent.availability === 'busy';
    };

    // Debounce para dar tempo do backend processar mudanças de status
    let historyRefreshTimeout = null;

    watch(() => props.agents, (newAgents, oldAgents) => {
      const changed = JSON.stringify(newAgents) !== JSON.stringify(oldAgents);

      if (changed) {
        // Limpa timeout anterior se existir
        if (historyRefreshTimeout) {
          clearTimeout(historyRefreshTimeout);
        }

        // Aguarda 500ms antes de recarregar histórico (dá tempo do backend processar)
        historyRefreshTimeout = setTimeout(() => {
          // Recarrega histórico de todos os agentes que estão expandidos
          Object.keys(expandedHistories.value).forEach(agentId => {
            if (expandedHistories.value[agentId]) {
              fetchAgentHistory(parseInt(agentId));
            }
          });

          // Carrega histórico automaticamente para agentes offline ou busy
          newAgents.forEach(agent => {
            if (needsHistoryLoaded(agent) && !agentHistories.value[agent.id]) {
              fetchAgentHistory(agent.id);
            }
          });
        }, 500);
      }
    }, { deep: true });

    // Recarrega históricos quando o team_id mudar
    watch(() => props.teamId, (newTeamId, oldTeamId) => {
      if (newTeamId !== oldTeamId) {
        // Limpa históricos anteriores
        agentHistories.value = {};

        // Recarrega histórico de todos os agentes que estão expandidos
        Object.keys(expandedHistories.value).forEach(agentId => {
          if (expandedHistories.value[agentId]) {
            fetchAgentHistory(parseInt(agentId));
          }
        });

        // Recarrega histórico de agentes offline ou busy
        props.agents.forEach(agent => {
          if (needsHistoryLoaded(agent)) {
            fetchAgentHistory(agent.id);
          }
        });
      }
    });

    // Recarrega históricos quando lastUpdate mudar (auto-refresh)
    watch(() => props.lastUpdate, (newUpdate, oldUpdate) => {
      if (newUpdate && oldUpdate && newUpdate !== oldUpdate) {
        // Auto-refresh aconteceu - recarrega histórico dos agentes expandidos
        setTimeout(() => {
          const expandedIds = Object.keys(expandedHistories.value).filter(id => expandedHistories.value[id]);
          expandedIds.forEach(agentId => {
            fetchAgentHistory(parseInt(agentId));
          });
        }, 500);
      }
    });

    // Carrega histórico inicial para agentes offline ou busy
    const loadInitialHistories = () => {
      // Aguarda 300ms para garantir que o backend processou as mudanças recentes
      setTimeout(() => {
        props.agents.forEach(agent => {
          if (needsHistoryLoaded(agent) && !agentHistories.value[agent.id]) {
            fetchAgentHistory(agent.id);
          }
        });
      }, 300);
    };

    loadInitialHistories();

    return {
      expanded,
      sortKey,
      sortOrder,
      sortedAgents,
      toggleExpanded,
      sortBy,
      getInitials,
      formatDuration,
      getOpenCountClass,
      getCapacityBarClass,
      getStatusClass,
      getStatusLabel,
      handleAgentClick,
      isAgentSelected,
      navigateToConversation,
      expandedHistories,
      agentHistories,
      loadingHistories,
      toggleHistory,
      formatHistoryTimestamp,
      formatPeriodRange,
      formatHistoryDuration,
      getAgentOfflineDuration,
      getHistoryPeriodBorderClass,
    };
  },
};
</script>